import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const SECTION_LABELS = {
  tech_today: "Il mio lavoro di oggi (tecnici)", today: "Oggi", tech_reports: "Resoconti tecnici da convalidare", attention: "Da attenzionare", upcoming: "Prossimi impegni",
  crm: "CRM", commercial: "Commerciale", operations: "Operativo",
};

const SectionList = ({ sections, setSections, available }) => {
  const move = (i, d) => { const s = [...sections]; [s[i], s[i + d]] = [s[i + d], s[i]]; setSections(s); };
  return (
    <ul className="space-y-1.5" data-testid="dashboard-layout-list">
      {sections.map((s, i) => (
        <li key={s.key} className="flex items-center gap-3 rounded-lg border border-line px-3 py-2 text-sm" data-testid={`layout-item-${s.key}`}>
          <input type="checkbox" checked={s.visible} onChange={(e) => setSections(sections.map((x, j) => (j === i ? { ...x, visible: e.target.checked } : x)))} data-testid={`layout-toggle-${s.key}`} />
          <span className="flex-1">{SECTION_LABELS[s.key]}{available && !available.has(s.key) && <span className="ml-2 text-xs text-slate-400">(non disponibile per il tuo ruolo)</span>}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === 0} onClick={() => move(i, -1)} aria-label="Su" data-testid={`layout-up-${s.key}`}><ArrowUp className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" disabled={i === sections.length - 1} onClick={() => move(i, 1)} aria-label="Giù" data-testid={`layout-down-${s.key}`}><ArrowDown className="h-4 w-4" /></Button>
        </li>))}
    </ul>
  );
};

export const DashboardCustomize = ({ open, onOpenChange, available, onSaved }) => {
  const { me } = useAuth();
  const sa = me?.role?.key === "super_admin";
  const [target, setTarget] = useState(""), [roles, setRoles] = useState([]), [sections, setSections] = useState([]), [busy, setBusy] = useState(false);
  const url = target ? `/dashboard/layout/role/${target}` : "/dashboard/layout";
  useEffect(() => { if (open && sa) api.get("/admin/roles").then((r) => setRoles(r.data.roles || [])).catch(() => setRoles([])); }, [open, sa]);
  useEffect(() => { if (open) api.get(url).then((r) => setSections(r.data.sections)).catch((e) => toast.error(errorMessage(e))); }, [open, url]);
  const done = (msg) => { toast.success(msg); onOpenChange(false); if (!target) onSaved(); };
  const save = async () => { setBusy(true); try { await api.put(url, { sections }); done("Dashboard salvata"); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); } };
  const reset = async () => { setBusy(true); try { await api.delete(url); done("Dashboard ripristinata"); } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); } };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) setTarget(""); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="dashboard-customize-dialog">
        <DialogHeader><DialogTitle>Personalizza dashboard</DialogTitle>
          <DialogDescription>Scegli quali sezioni mostrare e in che ordine. Vengono mostrate solo le sezioni permesse dal tuo ruolo.</DialogDescription></DialogHeader>
        {sa && <select className="h-10 w-full rounded-control border border-line bg-white px-3 text-sm" value={target} onChange={(e) => setTarget(e.target.value)} data-testid="layout-target-select">
          <option value="">La mia dashboard</option>{roles.map((r) => <option key={r.key} value={r.key}>Predefinita per il ruolo: {r.name}</option>)}</select>}
        <SectionList sections={sections} setSections={setSections} available={target ? null : available} />
        <DialogFooter className="gap-2 sm:justify-between">
          <Button variant="outline" onClick={reset} disabled={busy} data-testid="layout-reset-btn">{target ? "Elimina predefinita del ruolo" : "Ripristina predefinita"}</Button>
          <Button onClick={save} disabled={busy} data-testid="layout-save-btn">Salva</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
